import React, { useEffect, useState } from "react";
import { Card, Table, Button, Form, Row, Col, Alert } from "react-bootstrap";
import {
    fetchFinancialYears,
    createFinancialYear,
    closeFinancialYear
} from "../../api/api";
import {useAccountingScope} from "./accountingscope";

const FinancialYears = () => {
    const scope = useAccountingScope();
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [name, setName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const loadYears = async () => {
        try {
            setLoading(true);
            const data = await fetchFinancialYears();
            setYears(data);
        } catch (err) {
            setError("Failed to load financial years");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadYears();
    }, []);

    const handleCreate = async () => {
        try {
            await createFinancialYear({
                name,
                start_date: startDate,
                end_date: endDate
            });
            setName("");
            setStartDate("");
            setEndDate("");
            await loadYears();
        } catch (err) {
            alert("Failed to create financial year");
        }
    };

    const handleClose = async (id) => {
        if (scope.currentStore?.code !== "WH" || scope.currentStore?.store_type !== "WAREHOUSE") {
            alert("Switch your active store to the Central Warehouse (WH) before closing a financial year.");
            return;
        }
        if (!window.confirm("Close this financial year? This cannot be undone.")) {
            return;
        }
        try {
            await closeFinancialYear(id);
            await loadYears();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to close financial year");
        }
    };

    return (
        <div className="mt-4">
            <h2 className="mb-4">Financial Years</h2>

            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant={scope.currentStore?.code === "WH" ? "info" : "warning"}>
                Financial-year closing is company-wide across every store, but it can only be initiated while your active store is the Central Warehouse (WH).
                Current active store: <strong>{scope.currentStore?.name || "Not available"}</strong>.
            </Alert>

            {/* CREATE NEW FY */}
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Create Financial Year</Card.Title>
                    <Row>
                        <Col md={3}>
                            <Form.Control
                                placeholder="Name (e.g. 2026)"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Button type="button" onClick={handleCreate}>Create</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* FY LIST */}
            <Card>
                <Card.Body>
                    <Card.Title>Existing Financial Years</Card.Title>

                    {loading ? (
                        <p>Loading…</p>
                    ) : (
                        <Table bordered hover>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Period</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {years.map((fy) => (
                                    <tr key={fy.id}>
                                        <td>{fy.name}</td>
                                        <td>
                                            {fy.start_date} → {fy.end_date}
                                        </td>
                                        <td>
                                            {fy.is_closed ? (
                                                <span className="text-danger">Closed</span>
                                            ) : (
                                                <span className="text-success">Open</span>
                                            )}
                                        </td>
                                        <td>
                                            {!fy.is_closed && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleClose(fy.id)}
                                                    disabled={scope.currentStore?.code !== "WH" || scope.currentStore?.store_type !== "WAREHOUSE"}
                                                >
                                                    Close
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default FinancialYears;
